/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../../common/widgets/Button';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import RangeSlider from '../../../../../../common/widgets/RangeSlider';
import { config } from '../../../../config';
import { PriceRange } from '../../../../models/PriceRange';
import { ProductListModel } from '../../../../models/Product';
import { resources } from '../../../../resources/resources';
import { hasPricesInRange } from '../helpers';

interface PriceRangeFilterProps {
  priceRange: PriceRange;
  prevRangeSelected?: PriceRange;
  handlePriceRangeFilterChanged: (range: PriceRange) => void;
  products?: ProductListModel[];
}

export default function PriceRangeFilter(
  props: Readonly<PriceRangeFilterProps>,
) {
  const { t: translate } = useTranslation();
  const {
    priceRange,
    prevRangeSelected,
    handlePriceRangeFilterChanged,
    products,
  } = props;

  const minPriceLabelRef = useRef<HTMLInputElement>(null);
  const maxPriceLabelRef = useRef<HTMLInputElement>(null);
  const prevRangeRef = useRef<PriceRange | null>(null);
  const prevRangesSelectedRef = useRef<Map<string, PriceRange>>(new Map());
  const externallyManaged = useRef<boolean>(true);

  const [isActiveSliding, setIsActiveSliding] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [range, setRange] = useState<PriceRange>(
    prevRangeSelected ?? priceRange,
  );
  const [labelRange, setLabelRange] = useState<PriceRange>(range);

  const handleRangeChange = (value: PriceRange) => {
    externallyManaged.current = false;
    setRange(value);
  };
  const handleMinRangeLabelChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replaceAll(/\D/g, '');
    while (value.startsWith('0') && value.length > 1) {
      value = value.slice(1);
    }

    setLabelRange(
      produce(d => {
        d.min = Number(value);
      }),
    );
  };

  const handleMaxRangeLabelChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replaceAll(/\D/g, '');
    while (value.startsWith('0') && value.length > 1) {
      value = value.slice(1);
    }

    setLabelRange(
      produce(d => {
        d.max = Number(value);
      }),
    );
  };

  const handleFocuse = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleApplyLabelsValue = () => {
    labelRange.min >= priceRange.min && labelRange.min <= labelRange.max
      ? setRange(
          produce(d => {
            d.min = labelRange.min;
          }),
        )
      : setLabelRange(
          produce(d => {
            d.min = range.min;
          }),
        );

    labelRange.max <= priceRange.max && labelRange.max >= labelRange.min
      ? setRange(
          produce(d => {
            d.max = labelRange.max;
          }),
        )
      : setLabelRange(
          produce(d => {
            d.max = range.max;
          }),
        );

    externallyManaged.current = false;
  };

  const calculateRange = (): PriceRange => {
    const minValue =
      prevRangeSelected &&
      prevRangeSelected.min > priceRange.min &&
      prevRangeSelected.min !== prevRangeRef.current?.min
        ? prevRangeSelected?.min
        : prevRangesSelectedRef.current.get(
            `${priceRange.min}-${priceRange.max}`,
          )?.min ?? priceRange.min;

    const maxValue =
      prevRangeSelected &&
      prevRangeSelected.max < priceRange.max &&
      prevRangeSelected.max !== prevRangeRef.current?.max
        ? prevRangeSelected.max
        : prevRangesSelectedRef.current.get(
            `${priceRange.min}-${priceRange.max}`,
          )?.max ?? priceRange.max;

    return { min: minValue, max: maxValue } as PriceRange;
  };

  useEffect(() => {
    if (minPriceLabelRef.current) {
      minPriceLabelRef.current.style.width = `${
        labelRange.min.toString().length + 1
      }ch`;
    }
  }, [labelRange.min, isExpanded]);

  useEffect(() => {
    if (maxPriceLabelRef.current) {
      maxPriceLabelRef.current.style.width = `${
        labelRange.max.toString().length + 1
      }ch`;
    }
  }, [labelRange.max, isExpanded]);

  useEffect(() => {
    const minValue =
      prevRangeSelected && prevRangeSelected?.min >= priceRange.min
        ? prevRangeSelected?.min
        : priceRange.min;
    const maxValue =
      prevRangeSelected && prevRangeSelected?.max <= priceRange.max
        ? prevRangeSelected?.max
        : priceRange.max;

    if (
      minValue !== prevRangeSelected?.min &&
      maxValue !== prevRangeSelected?.max
    ) {
      setRange({ min: minValue, max: maxValue });
    }

    prevRangesSelectedRef.current.set(`${priceRange.min}-${priceRange.max}`, {
      min: minValue,
      max: maxValue,
    });
    externallyManaged.current = true;
  }, [prevRangeSelected]);

  useEffect(() => {
    if (_.isEqual(priceRange, prevRangeSelected)) {
      prevRangesSelectedRef.current.delete(
        `${priceRange.min}-${priceRange.max}`,
      );
    }

    setRange(() => calculateRange());

    prevRangeRef.current = priceRange;
    externallyManaged.current = true;
  }, [priceRange]);

  useEffect(() => {
    if (
      !isActiveSliding &&
      products &&
      hasPricesInRange(products, range, priceRange) &&
      !externallyManaged.current
    ) {
      handlePriceRangeFilterChanged(range);
    }
  }, [handleRangeChange]);

  useEffect(() => {
    setLabelRange(range);
  }, [range.min, range.max]);

  return (
    <Loader loading={!priceRange} loader={<LoadingSpinner />}>
      <div
        className={`action-panel price-range-panel expandable expand-on-arrow ${
          isExpanded ? '' : 'collapsed'
        }`}
      >
        <div className="action-panel-title">
          <span>{translate(resources.filters.priceRangeTitle)}</span>
          <span
            className="action-panel-expand"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icons.ArrowUp />
          </span>
        </div>
        {isExpanded && (
          <div className="action-panel-content">
            <RangeSlider
              min={priceRange.min}
              max={priceRange.max}
              value={range}
              onChangeValue={handleRangeChange}
              activeSliding={setIsActiveSliding}
              label={
                <>
                  <div className="slider-label">
                    <input
                      className="editable-label-input"
                      type="text"
                      inputMode="numeric"
                      value={labelRange.min}
                      onChange={handleMinRangeLabelChange}
                      onFocus={handleFocuse}
                      ref={minPriceLabelRef}
                    />
                    <span>{`${config.currency}`}</span>
                  </div>
                  <div className="slider-label">
                    <input
                      className="editable-label-input"
                      type="text"
                      inputMode="numeric"
                      value={labelRange.max}
                      onChange={handleMaxRangeLabelChange}
                      onFocus={handleFocuse}
                      ref={maxPriceLabelRef}
                    />
                    <span>{`${config.currency}`}</span>
                  </div>
                  <Button
                    className="btn btn-solid apply-range"
                    disabled={
                      (range.min === labelRange.min &&
                        range.max === labelRange.max) ||
                      isActiveSliding
                    }
                    icon={<Icons.ArrowRight />}
                    onClick={handleApplyLabelsValue}
                  ></Button>
                </>
              }
            />
          </div>
        )}
      </div>
    </Loader>
  );
}
